import React, {ReactElement, useRef} from "react";
import {Button, ButtonBase, Typography} from "@mui/material";
import {AddPhotoAlternateOutlined} from "@mui/icons-material";
import {PD_LG, PD_SM} from "../../shared/dimens";
import {mediumGray} from "../../shared/colors";
import {UploadedPhoto} from "./types";

function PhotoThumbnail({photo, onAdd}: {
  photo: UploadedPhoto;
  onAdd: (photo: UploadedPhoto) => void;
}): ReactElement {
  return (
    <ButtonBase
      onClick={() => onAdd(photo)}
      style={{
        overflow: "hidden",
        aspectRatio: "1",
        border: "2px solid transparent",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "#000066";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor = "transparent";
      }}>
      <img
        src={photo.src}
        alt={photo.name}
        style={{width: "100%", height: "100%", objectFit: "cover", display: "block"}}
      />
    </ButtonBase>
  );
}

export function PhotosPanel(props: {
  photos: UploadedPhoto[];
  onPhotoUploaded: (photo: UploadedPhoto) => void;
  onPhotoAdd: (photo: UploadedPhoto) => void;
}): ReactElement {
  const {photos, onPhotoUploaded, onPhotoAdd} = props;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) {
      return;
    }
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const src = ev.target?.result as string;
        const img = new window.Image();
        img.onload = () => {
          onPhotoUploaded({
            id: `${Date.now()}_${file.name}`,
            src,
            htmlImage: img,
            name: file.name,
          });
        };
        img.src = src;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  return (
    <div style={{display: "flex", flexDirection: "column", gap: PD_SM}}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{display: "none"}}
        onChange={handleFileChange}
      />
      <Button
        variant="outlined"
        startIcon={<AddPhotoAlternateOutlined/>}
        onClick={() => fileInputRef.current?.click()}
        style={{width: "100%"}}
      >
        Upload photos
      </Button>
      {photos.length === 0
        ? <Typography variant={"caption"} style={{color: mediumGray, textAlign: "center"}}>
          Upload photos to add them to your design
        </Typography>
        : <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: PD_SM,
        }}>
          {photos.map(photo => (
            <PhotoThumbnail key={photo.id} photo={photo} onAdd={onPhotoAdd}/>
          ))}
        </div>
      }
    </div>
  );
}